// cSpell:ignore forfait Provisies
// A provision the broker adds by hand to a tenant in the Provisies step
// (Lewis, 2026-10-16): a description and its amount for this settlement,
// both required, the amount not 0, and the kind it counts in (decision 20,
// 2026-10-20), never a kind the lease has no provision for (a forfait in
// the engine, never named as one: Lewis, 2026-10-20). The form says what is
// missing next to the field; nothing is kept until all are right.

import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { roundCents } from "./money.utils";
import { IExtraProvision } from "./types";

export type TExtraProvisionValues = Pick<
  IExtraProvision,
  "title" | "amount" | "kind"
>;

// The kinds the broker can add a provision to for a tenant (decision 20,
// 2026-10-20): the slices the settlement weighs for that lease. A kind the
// lease has no provision for (forfaitKinds) is shown but cannot be chosen,
// with the reason in provision terms.
export interface IExtraProvisionKindOption {
  kind: "common" | "private";
  // Why it cannot be chosen; absent when it can.
  disabledReason?: string;
}

const KIND_NO_PROVISION_KEYS: Record<"common" | "private", string> = {
  common: "settlement.provisions.extra_form.kind_no_provision_common",
  private: "settlement.provisions.extra_form.kind_no_provision_private",
};

export const extraProvisionKindOptions = (params: {
  slices: Array<"common" | "private">;
  fixedFeeKinds: Array<"common" | "private">;
  tenantName: string;
}): IExtraProvisionKindOption[] =>
  (["common", "private"] as const)
    .filter(kind => params.slices.includes(kind))
    .map(kind =>
      params.fixedFeeKinds.includes(kind)
        ? {
            kind,
            disabledReason: getLocalizedText(KIND_NO_PROVISION_KEYS[kind], {
              tenant: params.tenantName,
            }),
          }
        : { kind },
    );

// The kind a new provision starts on: the private kind as before, else the
// first one that can be chosen.
export const defaultExtraProvisionKind = (
  options: IExtraProvisionKindOption[],
): "common" | "private" | undefined => {
  const open = options.filter(option => !option.disabledReason);
  return (open.find(option => option.kind === "private") ?? open[0])?.kind;
};

export interface IExtraProvisionCheck {
  // What the form says under each field, when something is wrong.
  errors: { title?: string; amount?: string; kind?: string };
  // What to keep, once nothing is wrong.
  values?: TExtraProvisionValues;
}

export const checkExtraProvision = (input: {
  title: string;
  // Undefined while the field is empty or cannot be read.
  amount?: number;
  // The kind chosen, and whether it may be chosen; undefined when none can.
  kind?: "common" | "private";
  kindOptions?: IExtraProvisionKindOption[];
}): IExtraProvisionCheck => {
  const title = input.title.trim();
  const { amount, kind, kindOptions } = input;
  const kindAllowed =
    !kindOptions ||
    kindOptions.some(option => option.kind === kind && !option.disabledReason);
  const errors = {
    ...(!kindAllowed
      ? {
          kind: getLocalizedText(
            "settlement.provisions.extra_form.kind_required",
          ),
        }
      : {}),
    ...(title === ""
      ? {
          title: getLocalizedText(
            "settlement.provisions.extra_form.description_required",
          ),
        }
      : {}),
    ...(amount == null
      ? {
          amount: getLocalizedText(
            "settlement.provisions.extra_form.amount_required",
          ),
        }
      : {}),
    ...(amount != null && roundCents(amount) === 0
      ? {
          amount: getLocalizedText(
            "settlement.provisions.extra_form.amount_not_zero",
          ),
        }
      : {}),
  };
  if (errors.title || errors.amount || errors.kind || amount == null) {
    return { errors };
  }
  return {
    errors,
    values: { title, amount: roundCents(amount), ...(kind ? { kind } : {}) },
  };
};
